import { type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Tooltip } from "@fluentui/react-components";
import {
  ColorArea,
  ColorPicker as FluentColorPicker,
  ColorSlider,
} from "@fluentui/react-components";
import { ArrowLeft16Regular } from "@fluentui/react-icons/svg/arrow-left";
import { Checkmark16Filled } from "@fluentui/react-icons/svg/checkmark";
import { Color20Regular } from "@fluentui/react-icons/svg/color";
import type { ColorSwatch } from "@onetasklist/shared/colors";
import { useColorSwatchText } from "@onetasklist/shared/colors";
import { getContrastingTextColor } from "@onetasklist/shared";
import { useColorPickerViewModel } from "./ColorPicker.ViewModel";
import type { HsvColor } from "./ColorPicker.ViewModel";
import "./ColorPicker.scss";

interface ColorPickerProps {
  colors: ColorSwatch[];
  selected?: string;
  onSelect: (value: string) => void;
  includeUnset?: boolean;
  unsetLabel?: string;
  containerClass?: string;
  swatchBaseClass?: string;
  tooltipRelationship?: "label" | "description";
}

export default function ColorPicker({
  colors,
  selected,
  onSelect,
  includeUnset = false,
  unsetLabel = "Unset",
  containerClass = "color-picker__swatchGrid",
  swatchBaseClass = "color-picker__swatch",
  tooltipRelationship = "label",
}: ColorPickerProps) {
  const { t } = useTranslation();
  const colorText = useColorSwatchText(t);
  const vm = useColorPickerViewModel({ colors, selected, onSelect });
  const base = swatchBaseClass;

  return (
    <div className="color-picker">
      {!vm.showCustom && (
        <div className={containerClass}>
          {includeUnset && (
            <Tooltip
              key="unset"
              content={unsetLabel}
              relationship={tooltipRelationship}
            >
              <Button
                shape="rounded"
                className={`${base} ${base}--unset ${!selected ? `${base}--selected` : ""}`}
                aria-label={unsetLabel}
                onClick={vm.handleUnsetSelect}
                style={{
                  backgroundColor: "transparent",
                  color: "var(--colorNeutralForeground3, #6b6b6b)",
                }}
                icon={!selected ? <Checkmark16Filled /> : <></>}
              />
            </Tooltip>
          )}

          {colors.map((c) => {
            const bg = c.value ?? undefined;
            const fg = bg ? getContrastingTextColor(bg) : undefined;
            const isSelected =
              !!selected &&
              !!c.value &&
              selected.toLowerCase() === c.value.toLowerCase();
            return (
              <Tooltip
                key={c.id}
                content={colorText.getColorLabelText(c)}
                relationship={tooltipRelationship}
              >
                <Button
                  shape="rounded"
                  className={`${base} ${base}--${c.id} ${isSelected ? `${base}--selected` : ""}`}
                  aria-label={colorText.getColorLabelText(c)}
                  onClick={() => vm.handleSwatchSelect(c.value ?? "")}
                  style={bg ? { backgroundColor: bg, color: fg } : undefined}
                  icon={isSelected ? <Checkmark16Filled /> : <></>}
                />
              </Tooltip>
            );
          })}
        </div>
      )}

      <div className="color-picker__customRow">
        <Button
          size="small"
          appearance="subtle"
          icon={vm.showCustom ? <ArrowLeft16Regular /> : <Color20Regular />}
          className={`color-picker__customToggle${vm.showCustom || vm.isCustomSelected ? " color-picker__customToggle--active" : ""}`}
          onClick={() => (vm.showCustom ? vm.closeCustom() : vm.openCustom())}
        >
          {vm.showCustom ? "Back to palette" : "Custom colour"}
        </Button>
        {vm.isCustomSelected && !vm.showCustom && (
          <span
            className="color-picker__customDot"
            style={{ "--cp-dot-bg": selected } as CSSProperties}
          />
        )}
      </div>

      {vm.showCustom && (
        <div className="color-picker__customPanel">
          <FluentColorPicker
            color={vm.customHsv}
            onColorChange={(_, data) =>
              vm.handleCustomColorChange(data.color as HsvColor)
            }
          >
            <ColorArea />
            <ColorSlider />
          </FluentColorPicker>
          <div className="color-picker__hexRow">
            <span className="color-picker__hexHash">#</span>
            <Input
              size="small"
              className="color-picker__hexInput"
              value={vm.hexInput}
              onChange={(_, d) => vm.handleHexInputChange(d.value)}
              maxLength={6}
              placeholder={t("components.colorPicker.hexInputPlaceholder", "rrggbb")}
            />
            {/^[0-9a-fA-F]{6}$/.test(vm.hexInput) && (
              <span
                className="color-picker__hexPreview"
                style={
                  { "--cp-preview-bg": `#${vm.hexInput}` } as CSSProperties
                }
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
